﻿import React, { useMemo, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Context, { context_type } from '@Util/Context';
import { str_isEmpty } from '@Util/Utils';
import * as Api from '@Util/Api';
import { apiBase } from '@Util/GlobalValues';
import {
    Popup_Add_Reminder_Props,
    Select_Option,
    Reminder_Type,
    Recurring_Type
} from '@Util/Types';

import Popup from '@Elements/Popup/Popup';
import Datepicker_With_Label from '@Elements/FormElements/Datepicker_With_Label';
import Textarea_With_Label from '@Elements/FormElements/Textarea_With_Label';
import Select_With_Label from '@Elements/FormElements/Select_With_Label';
import Submit_Button from '@Elements/FormElements/Submit_Button';
import Checkbox from '@Elements/FormElements/Checkbox';

export default function Add_Reminder_Popup(props: Popup_Add_Reminder_Props): JSX.Element {
    const { t } = useTranslation();
    const {
        fetch_reminders,
        set_status_update,
        logged_in_user
    }: context_type = useContext(Context);

    const tomorrow_at_noon: string = useMemo(() => {
        const tomorrow: Date = new Date();
        tomorrow.setDate(new Date().getDate() + 1);
        tomorrow.setHours(12, 0, 0, 0);
        return tomorrow.toISOString();
    }, []);

    const [type_of_reminder, set_type_of_reminder] = useState<Reminder_Type>("Personal");
    const [recurring, set_recurring] = useState<Recurring_Type>("None");
    const [content, set_content] = useState<string>("");
    const [due_date, set_due_date] = useState<string>(tomorrow_at_noon);
    const [create_as_indivual_reminders_to_all_users, set_create_as_individual_reminders_to_all_users] = useState<boolean>(false);
    const [requires_only_one_individual_to_complete, set_requires_only_one_individual_to_complete] = useState<boolean>(false);
    const [error, set_error] = useState<string | null>(null);
    const [saving, set_saving] = useState<boolean>(false);

    const type_of_reminder_options: Select_Option[] = useMemo(() => {
        return [
            { value: "Personal", text: t('Label.Personal') },
            { value: "Global", text: t('Label.Global') }
        ];
    }, []);

    const recurring_options: Select_Option[] = useMemo(() => {
        return [
            { value: "None", text: t('Label.None') },
            { value: "Daily", text: t('Label.Daily') },
            { value: "Weekly", text: t('Label.Weekly') },
            { value: "Monthly", text: t('Label.Monthly') },
            { value: "Yearly", text: t('Label.Yearly') },
        ];
    }, []);

    function reset_form_values(): void {
        set_content("");
        set_recurring("None");
        set_due_date(tomorrow_at_noon);
        set_type_of_reminder("Personal");
        set_create_as_individual_reminders_to_all_users(false);
        set_requires_only_one_individual_to_complete(false);
    }

    const handle_add_reminder = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;

        if (str_isEmpty(content)) {
            set_error(t('Text.FillInReminderText'));
            return;
        }

        if (str_isEmpty(due_date)) {
            set_error(t('Text.FillInDueDate'));
            return;
        }

        set_saving(true);
        set_error(null);

        try {
            const api_call = await Api.callApi(`${apiBase}/CreateReminder`, {
                content: content,
                dueDate: due_date,
                type: type_of_reminder,
                recurring: recurring,
                createAsIndividualRemindersForAllUsers: create_as_indivual_reminders_to_all_users,
                requiresOnlyOneIndividualToComplete: requires_only_one_individual_to_complete
            });

            if (api_call?.status === "Invalid" || api_call?.status === "Error") {
                set_status_update({
                    visible: true,
                    text: api_call?.details,
                    type: "Error"
                });
            } else {
                props.set_open(false);
                reset_form_values();

                set_status_update({
                    visible: true,
                    text: t('Text.ApiSaved'),
                    type: "Success"
                });

                fetch_reminders();
            }
        } catch (err) {
            set_status_update({
                visible: true,
                text: err.message || t('Error.UnknownError'),
                type: "Error"
            });
        } finally {
            set_saving(false);
        }
    }, [type_of_reminder, content, due_date, saving, set_status_update, t, props]);

    return (
        <Popup
            open={props.open}
            set_open={props.set_open}
            title={t('Text.AddReminder')}
            disable_close_action={false}
        >
            <form onSubmit={handle_add_reminder}>
                <Select_With_Label
                    id="add_reminder_type"
                    label={t('Label.Type')}
                    value={type_of_reminder}
                    onChange={(e) => {
                        set_type_of_reminder(e.target.value);
                        set_create_as_individual_reminders_to_all_users(false);
                    }}
                    error={Boolean(error)}
                    options={type_of_reminder_options}
                />
                <Select_With_Label
                    id="add_reminder_recurring"
                    label={t('Label.Recurring')}
                    value={recurring}
                    onChange={(e) => {
                        set_recurring(e.target.value);
                    }}
                    error={Boolean(error)}
                    options={recurring_options}
                />
                <Datepicker_With_Label
                    id="add_project_due_date"
                    label={t('Label.DueDate')}
                    selected={due_date}
                    onChange={(date) => set_due_date(date.toISOString())}
                    error={Boolean(error)}
                    minDate={new Date()}
                />
                {
                    type_of_reminder === "Global" &&
                    <Checkbox
                        id="add_reminder_requires_only_one_individual_to_complete"
                        checked={requires_only_one_individual_to_complete}
                        onChange={() => set_requires_only_one_individual_to_complete(!requires_only_one_individual_to_complete)}
                        label={t('Label.RequiresOnlyOneIndividualToComplete')}
                        error={Boolean(error)}
                    />
                }
                {
                    type_of_reminder === "Global" && logged_in_user.isAdmin &&
                    <Checkbox
                        id="add_reminder_create_as_individual_reminders_to_all_users"
                        checked={create_as_indivual_reminders_to_all_users}
                        onChange={() => set_create_as_individual_reminders_to_all_users(!create_as_indivual_reminders_to_all_users)}
                        label={t('Label.CreateAsIndividualRemindersToAllUsers')}
                        error={Boolean(error)}
                    />
                }
                <Textarea_With_Label
                    id="add_reminder_content"
                    label={t('Label.ReminderText')}
                    value={content}
                    onChange={(e) => set_content(e.target.value)}
                    error={Boolean(error)}
                    rows={5}
                />
                <Submit_Button
                    text={t('Button.Save')}
                    disabled={saving}
                    loading={saving}
                />
                {
                    error &&
                    <div className="error-message">{error}</div>
                }
            </form>
        </Popup>
    );
}